import React, { ReactNode } from 'react'
import classnames from 'classnames'
import kebabCase from 'lodash.kebabcase'
import camelCase from 'lodash/camelCase'
import { CTALink } from 'marketing-site/src/mvp/library/elements/CTALink'
import { mq, MVPColorIdTokenType, MVP_COLOR_TOKENS } from 'marketing-site/src/library/utils'
import { IProps } from './index'
import CardRenderer from './CardRenderer'
import { CTATheme } from 'marketing-site/src/mvp/library/utils/constants/CTATheme'
import {
  GridContainer,
  IProps as IGridContainerProps,
} from 'marketing-site/src/mvp/library/components/GridContainer'
import { Document } from '@contentful/rich-text-types'
const contentGridGap = '2rem'

export const CrosslinkCards: React.FC<IProps> = ({
  layout = '3-3',
  eyebrow,
  heading,
  subheading,
  ctaLink,
  cards,
  ctaLinkPosition = 'below-header',
  backgroundColor = 'paper',
  centerLastRow = 'center',
}: IProps) => {
  const classes = classnames('crosslink-cards', 'grid-layout', {
    [`crosslink-cards--layout-${layout}`]: layout,
    [`mvp-background-color-${kebabCase(backgroundColor)}`]: backgroundColor,
  })

  const ctaFill = ['deepTeal', 'slate', 'blue'].includes(backgroundColor)
    ? CTATheme.PaperOutline
    : CTATheme.SlateOutline

  const transformedCards = cards.map((card, index) => (
    <CardRenderer
      key={`card_${index}`}
      card={{
        ...card,
        backgroundColor: backgroundColor === 'paper' ? 'stone' : 'paper',
      }}
    />
  ))

  return (
    <div className={classes}>
      <div className="crosslink-cards__inner">
        <GridContainer
          layout={layout}
          eyebrow={eyebrow}
          title={heading}
          subhead={subheading as Document}
          titleSize="heading-2"
          titleWidth="default"
          ctaPosition={ctaLinkPosition as IGridContainerProps['ctaPosition']}
          body={transformedCards}
          bodyAlignment={centerLastRow}
          backgroundColor={camelCase(backgroundColor) as MVPColorIdTokenType}
          cta={ctaLink ? ((<CTALink {...ctaLink} variant={ctaFill} />) as ReactNode) : undefined}
        />
      </div>
      <style jsx>
        {`
          .crosslink-cards {
            padding-left: 1.25rem;
            padding-right: 1.25rem;

            :global(.grid-container) {
              grid-column: 1 / -1;
              padding: 0;
            }

            :global(.grid-layout > [class$='inner']) {
              max-width: unset;
            }

            :global(.mvp-background-color-slate
                span.text-size-subhead-lg, .mvp-background-color-slate
                .text-size-subhead-sm, .mvp-background-color-slate
                .text-size-body, .mvp-background-color-deep-teal
                .text-size-subhead-lg, .mvp-background-color-deep-teal .text-size-subhead-sm) {
              color: var(--paper);
            }
            :global(&.mvp-background-color-blue .text-size-body, &.mvp-background-color-slate
                .text-size-body, &.mvp-background-color-deep-teal .text-size-body) {
              color: var(--neutral-700);
            }

            &__inner {
              max-width: $container-max-width;
              margin: 0 auto;
              display: grid;

              grid-column-gap: 1rem;
              position: relative;
            }

            &__below-the-content-container {
              grid-column: 3 / span 8;
              max-width: 100%;
              margin: 0 auto;
            }

            &__eyebrow {
              margin-bottom: 1.5rem;
              text-align: center;
            }

            &__headline {
              text-align: center;
            }

            &__cta-link {
              margin-top: 2.5rem;
              text-align: center;
            }

            &__content + &__cta-link {
              margin-top: 6.25rem;
            }

            &__subtitle {
              text-align: center;
              margin: 1.5rem auto 0;
              font-weight: 400;
              line-height: 1.5;
              letter-spacing: 0.4px;
              color: ${MVP_COLOR_TOKENS.neutral700.hex};
            }

            &__footnote {
              grid-column: 1/-1;
              display: block;
              text-align: center;
              margin-top: 2.25rem;
            }
          }

          :global(.grid-container__item) {
            display: flex;
            height: 100%;
            width: 100%;
          }

          .card-wrapper.border-right {
            position: relative;
          }

          .card-wrapper.border-right::after {
            content: '';
            height: 0.125rem;
            position: absolute;
            right: 0;
            bottom: calc(-${contentGridGap} / 2);
            left: 0;
            background-color: ${MVP_COLOR_TOKENS.stone.hex};
          }

          .card-wrapper.card--isHiddenOnMobile::after,
          .card-wrapper.border-right:last-child::after {
            content: none;
          }

          :global(.card-wrapper > div:first-child) {
            width: 100%;
          }

          :global(.card-wrapper > .card, .card-wrapper > .cta-link) {
            height: 100%;
          }

          :global(.card-wrapper > .card, .card-wrapper > .cta-button) {
            height: 100%;
          }

          :global(.icon-card) {
            width: 100%;
          }

          @media (${mq.mobile}) {
            .crosslink-cards {
              &__header {
                grid-column: 1 / -1;
              }

              &__eyebrow {
                margin-bottom: 1rem;
              }

              .card-wrapper {
                grid-column: 1fr;
              }

              :global(&__cta-link a.cta-link) {
                width: 100%;
              }

              &__below-the-content-container {
                width: 100%;
                grid-column: 1 / -1;
              }
            }
          }

          @media (${mq.tablet}) {
            .crosslink-cards {
              padding-left: 2rem;
              padding-right: 2rem;

              &__inner {
                grid-template-columns: repeat(12, 1fr);
                grid-column-gap: 1.75rem;
              }

              &__header {
                grid-column: 2 / -2;
              }
            }

            .card-wrapper.border-right::after {
              width: 2px;
              height: 100%;
              top: 0;
              right: calc(-${contentGridGap} / 2);
              bottom: 0;
              left: auto;
            }

            :global(.icon-card__content, .eyebrow-card__content) {
              display: flex;
              flex-direction: column;
            }

            :global(.icon-card____cta, .eyebrow-card____cta) {
              margin-top: auto;
            }
          }

          @media (${mq.desktop}) {
            .crosslink-cards {
              padding-left: 2.5rem;
              padding-right: 2.5rem;

              &__inner {
                grid-column-gap: 2rem;
              }
            }
          }
        `}
      </style>
    </div>
  )
}
